import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { PermissionControlService } from '@mirage-projects/cw-commons';
import { TreeNode, TreeviewCheckedSelection } from '@mirage-projects/cw-models';

import { TreeviewActionService } from '../treeview-action/treeview-action.service';
import { TreeviewService } from '../treeview.service';

/**
 * Treeview item component
 */
@Component({
    selector: 'cw-treeview-item',
    templateUrl: 'treeview-item.component.html',
    styleUrls: ['treeview-item.component.scss'],
    standalone: false,
})
export class TreeviewItemComponent implements OnInit {
    /** Tree definition data object */
    @Input()
    set data(value: TreeNode[]) {
        this._data = value;
        this.checkVisibleChildrenForNodes();
    }
    get data(): TreeNode[] {
        return this._data;
    }

    /** Enables multiple node selection */
    @Input() multiSelection = true;
    /** Tells if the parent node is unchecked, to make this one as inactive */
    @Input() parentUnchecked = false;
    /** Sets the treeview read only */
    @Input() isReadonly = false;
    /** Treeview icon size */
    @Input() iconSize: number | undefined;

    /** Selected item from tree */
    @Output() selectedItem = new EventEmitter<TreeNode>();
    /** Emits when item's checkbox is clicked */
    @Output() itemCheckedChange = new EventEmitter<{ node: TreeNode; checked: boolean }>();

    searchText$: Observable<string>;
    visibleChildrenMap: Map<TreeNode, boolean> = new Map();

    saveLayerNameWithDebounce = new Subject<{ node: TreeNode; newName: string }>();

    private _data: TreeNode[] = [];

    constructor(
        private treeviewActionService: TreeviewActionService,
        public treeviewService: TreeviewService,
        private permissionControlService: PermissionControlService
    ) {
        this.searchText$ = treeviewActionService.searchText$;
    }

    ngOnInit(): void {
        this.data.forEach((node) => {
            node.isEditable = this.checkEditableNode(node);
        });
    }

    expandClick(node: TreeNode): void {
        node.collapsed = !node.collapsed;
    }

    checkVisibleChildrenForNodes(): void {
        this.visibleChildrenMap = new Map();

        if (!this.data) {
            return;
        }

        for (const node of this.data) {
            const isVisible = this.checkVisibleChildren(node);

            this.visibleChildrenMap.set(node, isVisible);
        }
    }

    selectItem(node: TreeNode): void {
        this.selectedItem.emit(node);
    }

    checkboxChecked(node: TreeNode, checked: boolean): void {
        const data: TreeviewCheckedSelection = { node, checked };

        this.itemCheckedChange.emit(data);
    }

    checkItem(node: TreeviewCheckedSelection): void {
        this.itemCheckedChange.emit(node);
    }

    /**
     * Checks if the current node is inactive, or the parent one is unchecked
     * @param node the current node
     */
    isCheckboxDisabled(node: TreeNode): boolean {
        const isInactive = node.inactive ?? false;
        return isInactive || this.parentUnchecked || this.isReadonly;
    }

    hasVisibleChildren(node: TreeNode): boolean {
        return this.visibleChildrenMap.get(node) ?? false;
    }

    /**
     * Emits the new node with the title changed and cancells the edit
     * @param node
     * @param event
     */
    onNameChange(node: TreeNode): void {
        this.treeviewActionService.nodeRename(node);
        this.treeviewService.nodeEditing = null;
    }

    /**
     *  function to exit saving or exit without saving the changes
     * @param event
     * @param node
     */
    onKeyDown(event: KeyboardEvent, node: TreeNode): void {
        if (event.key === 'Escape') {
            this.cancelEditing(node);
        } else if (event.key === 'Enter') {
            this.onNameChange(node);
        }
    }

    /**
     * Function to check if the user has permission to change the label name
     * @param node
     * @returns
     */
    private hasWritePermissions(node: TreeNode): boolean {
        return this.permissionControlService.canWriteByPermissions(node.data);
    }

    private cancelEditing(node: TreeNode): void {
        node.title = this.treeviewService.oldName;
        this.treeviewService.nodeEditing = null;
        this.treeviewService.oldName = '';
    }

    /**
     * Returns true if the node has any child or grandchild visible
     * @param node
     * @returns
     */
    private checkVisibleChildren(node: TreeNode): boolean {
        return node.children?.some((child) => child.visible || this.checkVisibleChildren(child)) ?? false;
    }

    private checkEditableNode(node: TreeNode): boolean {
        const hasChildren = !!node.children?.length;
        const isLayer = !!node.data?._layerObject?.layerType;

        return !hasChildren && isLayer && this.hasWritePermissions(node);
    }
}
